import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { beginLogin, useAuth } from '../lib/auth'
import { api, type WhoAmI, ApiError } from '../lib/api'
import { Masthead } from '../components/Masthead'

/**
 * Login route — full-bleed Lopettia masthead with a centred auth card
 * below. Visually parallels the original FastAPI overlay that locks the
 * page until a session is present.
 */
export function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, setSession } = useAuth()
  const [checking, setChecking] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function probe() {
      if (!isAuthenticated) {
        setChecking(false)
        return
      }
      try {
        const me = await api<WhoAmI>('/api/v1/whoami')
        if (cancelled) return
        setSession(useAuth.getState().token!, {
          sub: me.sub,
          email: me.email,
          partnerId: me.partner_id,
          roles: me.roles,
        })
        navigate('/dashboard', { replace: true })
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 401) {
          useAuth.getState().clearSession()
        }
        setChecking(false)
      }
    }
    void probe()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, navigate, setSession])

  async function handleSignIn() {
    setError(null)
    try {
      await beginLogin()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  return (
    <div className="wrap">
      <Masthead />

      <section className="auth-card">
        <div className="auth-card__inner">
          <div className="auth-card__label">{t('auth.login.title')}</div>
          <p className="auth-card__lede">{t('auth.login.subtitle')}</p>

          {checking ? (
            <p className="auth-card__hint">{t('auth.login.checking')}</p>
          ) : (
            <button type="button" className="auth-card__btn" onClick={handleSignIn}>
              {t('auth.login.submit')}
            </button>
          )}

          {error && <p className="auth-card__error">{error}</p>}
        </div>
      </section>
    </div>
  )
}
